/**
 * REF-6 — the Refunds by Staff card's real read path (GET /sales/live/v2/refund-by-staff).
 *
 * The endpoint doesn't split `totalRefunded` into item vs fee dollars, only counts — the
 * card itself never renders `itemAmount`/`feeAmount` (only the two counts and the total), so
 * both are set to 0 here rather than fabricated from a guessed split.
 *
 * Paginated: one flat page per request (`page` 1-indexed), ranked by total returned desc.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useBranch } from '../../../context/BranchContext';
import { useAuth } from '../../../context/AuthContext';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { fetchRefundByStaffRequest } from '../../../redux/reports/clickHouse/clickHouseActions';
import type { OrderSource } from '../../../redux/reports/clickHouse/clickHouseApi';
import type { StaffRefundRow, RefundSalesSource } from './refundModel';

const findToken = (obj: any, depth = 0): string | null => {
    if (!obj || typeof obj !== 'object' || depth > 3) return null;
    for (const key of ['accessToken', 'access_token', 'token', 'jwtToken', 'jwt', 'idToken', 'id_token']) {
        if (obj[key] && typeof obj[key] === 'string' && obj[key].length > 10) return obj[key];
    }
    for (const [, val] of Object.entries(obj)) {
        if (typeof val === 'string' && val.length > 50) return val;
        if (val && typeof val === 'object' && !Array.isArray(val)) {
            const found = findToken(val, depth + 1);
            if (found) return found;
        }
    }
    return null;
};

const toOrderSource = (source: RefundSalesSource): OrderSource | undefined =>
    source === 'direct' ? 'direct' : source === 'thirdparty' ? 'thirdparty' : undefined;

/** Rows per page — the endpoint's own default, so a typical outlet sees one page. */
export const REFUND_BY_STAFF_PAGE_SIZE = 10;

export interface UseRefundByStaffPeriodArgs {
    startDate?: string;
    endDate?: string;
    /** Defaults to 'overall'. */
    source?: RefundSalesSource;
    /** Defaults to REFUND_BY_STAFF_PAGE_SIZE. */
    pageSize?: number;
}

export interface UseRefundByStaffPeriodResult {
    /** This page only, ranked by total returned (the API's order). */
    staff: StaffRefundRow[];
    /** True only while there is nothing to show yet (first load / a new period). */
    loading: boolean;
    /** True while ANY request is in flight, including a page change that keeps rows on screen. */
    fetching: boolean;
    failure: boolean;
    retry: () => void;
    /** 1-indexed current page. */
    page: number;
    pageSize: number;
    totalPages: number;
    totalElements: number;
    setPage: (page: number) => void;
}

export function useRefundByStaffPeriod({ startDate, endDate, source = 'overall', pageSize = REFUND_BY_STAFF_PAGE_SIZE }: UseRefundByStaffPeriodArgs): UseRefundByStaffPeriodResult {
    const dispatch = useAppDispatch();
    const { selectedBranch: branch } = useBranch();
    const { auth } = useAuth();
    const locationId = branch?.id ?? branch?.locationId ?? '';
    const token = useMemo(() => (auth ? findToken(auth) : null), [auth]);

    const orderSource = toOrderSource(source);

    // The page belongs to a context (outlet / period / source / size); when it changes the stored
    // page reads back as page 1, so the new context is fetched once, at page 1.
    const contextKey = `${locationId}|${startDate ?? ''}|${endDate ?? ''}|${orderSource ?? ''}|${pageSize}`;
    const [pageState, setPageState] = useState({ key: contextKey, page: 1 });
    const page = pageState.key === contextKey ? pageState.page : 1;
    const setPage = (next: number) => setPageState({ key: contextKey, page: Math.max(1, next) });

    // A page change keeps the rows up while it loads; a context change (incl. the first fetch on mount, so rows
    // left in the store by an earlier visit never label this one) clears them.
    const lastContextRef = useRef<string | null>(null);

    const fetch = (keepPrevious = false) => {
        // Same guard as the other REF-2/REF-6 real-data hooks — locationId and token
        // resolve independently on mount; dispatching before both are ready double-hits it.
        if (!locationId || !token) return;
        dispatch(fetchRefundByStaffRequest(locationId, startDate, endDate, orderSource, page, pageSize, keepPrevious));
    };

    useEffect(() => {
        const sameContext = lastContextRef.current === contextKey;
        lastContextRef.current = contextKey;
        fetch(sameContext);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, locationId, startDate, endDate, orderSource, token, page, pageSize]);

    const raw = useAppSelector((s) => s.clickHouse.refundByStaffSuccess);
    const fetching = useAppSelector((s) => s.clickHouse.refundByStaffLoading);
    const failure = useAppSelector((s) => s.clickHouse.refundByStaffFailure);

    // Order is the API's (total desc) — not re-sorted, so a row never jumps between pages.
    const staff = useMemo<StaffRefundRow[]>(() => (raw?.content ?? []).map((r) => ({
        staffName: String(r.staffName ?? '').trim() || 'Unassigned',
        itemCount: Number(r.itemCount) || 0,
        feeCount: Number(r.feeCount) || 0,
        total: Number(r.totalRefunded) || 0,
        itemAmount: 0,
        feeAmount: 0,
    })), [raw]);

    return {
        staff, loading: fetching && !raw, fetching, failure, retry: () => fetch(false),
        page, pageSize, totalPages: Math.max(1, raw?.totalPages ?? 1), totalElements: raw?.totalElements ?? staff.length, setPage,
    };
}
